/**
 * Handoff section 06 — "Stacked dialogs: depth made legible with a level breadcrumb".
 *
 * WHAT THE DESIGN ASKS FOR
 * When a dialog opens on top of another (Configure Programme over New Franchisee), the top
 * one shows a breadcrumb of the levels — "New Franchisee › Configure Programme" — labels
 * the top one "LEVEL 2", and notes that Esc closes only that level. Without it, a second
 * modal over a first is just two overlapping white rectangles with no clue how deep you
 * are or what closing will do.
 *
 * WHY THIS IS A DOM WATCHER AND NOT A DIALOG SUBCLASS
 * Serenity creates dialogs from many places — EntityGrid, the column picker, the
 * permission editor, our own ProgrammeDialog — and they do not share a base we control.
 * Watching the document for modals appearing means every dialog gets the treatment,
 * including ones added later, in the same spirit as ListPageChrome hooking a static event
 * rather than editing each grid. Nothing here changes a dialog's behaviour; it only
 * annotates depth.
 *
 * ⚠ Serenity 10.3.6 creates no `.modal-backdrop` (see CLAUDE.md) — `.modal` is itself the
 * full-viewport scrim. So depth cannot be read from backdrops; it is counted from the
 * visible `.modal` elements themselves.
 */

const LEVEL_ATTR = "data-m-level";

function visibleModals(): HTMLElement[] {
    return Array.from(document.querySelectorAll<HTMLElement>(".modal"))
        .filter(m => m.offsetParent !== null || m.classList.contains("show"));
}

/** The dialog's own title, used as its breadcrumb label. */
function titleOf(modal: HTMLElement): string {
    const t = modal.querySelector<HTMLElement>(".modal-title, .s-DialogTitle");
    return (t?.textContent || "").trim() || "Dialog";
}

function restamp() {
    const modals = visibleModals();

    for (const m of modals) {
        m.querySelector(".m-dialog-levels")?.remove();
        m.removeAttribute(LEVEL_ATTR);
    }
    // One dialog is just a dialog — the breadcrumb only earns its space at depth.
    if (modals.length < 2) return;

    modals.forEach((m, i) => {
        m.setAttribute(LEVEL_ATTR, String(i + 1));
        // Only the topmost carries the breadcrumb; repeating it on the buried dialogs
        // would be noise the user cannot act on.
        if (i !== modals.length - 1) return;

        const header = m.querySelector<HTMLElement>(".modal-header");
        if (!header) return;

        const bar = document.createElement("div");
        bar.className = "m-dialog-levels";

        modals.forEach((other, j) => {
            if (j) bar.appendChild(Object.assign(document.createElement("span"),
                { className: "m-dialog-levels-sep", textContent: "›" }));
            const crumb = document.createElement("span");
            crumb.className = "m-dialog-levels-crumb" + (j === modals.length - 1 ? " is-current" : "");
            crumb.textContent = titleOf(other);
            bar.appendChild(crumb);
        });

        const hint = document.createElement("span");
        hint.className = "m-dialog-levels-hint";
        hint.textContent = "Esc closes this level only";
        bar.appendChild(hint);

        header.insertAdjacentElement("afterend", bar);
    });
}

export function installStackedDialogs() {
    // Modals are added and removed from body; a subtree observer is the only reliable
    // signal because Serenity does not raise a document-level event for dialog open.
    const observer = new MutationObserver(() => {
        // Coalesce: opening one dialog fires several mutations as it is built.
        clearTimeout((restampSoon as any)._t);
        (restampSoon as any)._t = setTimeout(restamp, 30);
    });
    observer.observe(document.body, { childList: true, subtree: false, attributes: false });

    // Class changes on .modal (show/hide) do not add or remove nodes, so watch those too.
    const classObserver = new MutationObserver(() => restampSoon());
    const watch = () => document.querySelectorAll(".modal").forEach(m =>
        classObserver.observe(m, { attributes: true, attributeFilter: ["class", "style"] }));
    setInterval(watch, 1000);
    watch();
}

function restampSoon() {
    clearTimeout((restampSoon as any)._t);
    (restampSoon as any)._t = setTimeout(restamp, 30);
}
